import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Typography,
    TextField,
    Button,
    Collapse,
} from '@mui/material';
import { LocalOffer, CheckCircle, Close } from '@mui/icons-material';
import { PALETTE, alpha } from '../theme';
import { resolveActivePromo } from '../promos/resolveActivePromo';
import { applyPromoCode, clearPromoCode } from '../actions/promoActions';

/**
 * Promo entry bar rendered above the product list.
 *
 * Two states:
 *   - INACTIVE  Compact "Have a promo code?" input + Apply button. The
 *               disclosure copy explains that Zazzle won't show a coupon
 *               at checkout — the discount comes from a separate listing,
 *               so the price they see on Zazzle IS already the promo price.
 *   - ACTIVE    Green ribbon: "PROMO15 applied — 15% off all products.
 *               You'll see the discounted price on the next page." plus a
 *               Clear button.
 *
 * A failed Apply (code typed that doesn't resolve to any active promo)
 * stays in INACTIVE state but shows a small "Sorry, that code isn't
 * active right now" hint under the input.
 */
const PromoCodeBar = () => {
    const dispatch = useDispatch();
    const promoState = useSelector((s) => s.promos || {});
    const { list: promos = [], hydrated, urlCode, enteredCode } = promoState;

    const activePromo = useMemo(
        () => resolveActivePromo({
            promos,
            now: Date.now(),
            urlCode,
            enteredCode,
        }),
        [promos, urlCode, enteredCode]
    );

    // Local "did the user just submit and it didn't match" hint state.
    const [inputValue, setInputValue] = useState('');
    const [lastSubmitFailed, setLastSubmitFailed] = useState(false);

    // When the URL code is present and matches a real promo, lift it into
    // the input field so it's visible. But don't lift codes the visitor
    // entered manually — those are already reflected in the active state.
    useEffect(() => {
        if (urlCode && !enteredCode) setInputValue(urlCode);
    }, [urlCode, enteredCode]);

    const handleApply = () => {
        const trimmed = inputValue.trim();
        if (!trimmed) return;
        dispatch(applyPromoCode(trimmed));
        // The action runs synchronously through the reducer, but resolveActivePromo
        // here re-runs on the next render. We optimistically check by re-resolving.
        const next = resolveActivePromo({
            promos,
            now: Date.now(),
            urlCode,
            enteredCode: trimmed,
        });
        setLastSubmitFailed(!next);
    };

    const handleClear = () => {
        dispatch(clearPromoCode());
        setInputValue('');
        setLastSubmitFailed(false);
    };

    if (!hydrated) return null;

    // ----- Active state ------------------------------------------------------
    if (activePromo) {
        const codeLabel = activePromo.code
            ? activePromo.code.toUpperCase()
            : (activePromo.name || 'Sale');
        return (
            <Box
                sx={{
                    maxWidth: 900,
                    mx: 'auto',
                    mb: { xs: 2, sm: 3 },
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1.5, sm: 1.75 },
                    borderRadius: 3,
                    backgroundColor: alpha('success', 0.10),
                    border: `1px solid ${alpha('success', 0.35)}`,
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 2 },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                    <CheckCircle sx={{ color: PALETTE.success }} />
                    <Box>
                        <Typography sx={{ fontWeight: 700, color: PALETTE.text, lineHeight: 1.2 }}>
                            {codeLabel} applied — {activePromo.discountPercent}% off all products
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: PALETTE.textBody, display: 'block', mt: 0.25 }}
                        >
                            The discount is already baked into the product price you'll see on Zazzle —
                            there's no coupon field at checkout.
                        </Typography>
                    </Box>
                </Box>
                <Button
                    onClick={handleClear}
                    size="small"
                    startIcon={<Close />}
                    sx={{ color: PALETTE.textMuted, textTransform: 'none' }}
                >
                    Remove
                </Button>
            </Box>
        );
    }

    // ----- Inactive state ----------------------------------------------------
    return (
        <Box
            sx={{
                maxWidth: 900,
                mx: 'auto',
                mb: { xs: 2, sm: 3 },
                px: { xs: 2, sm: 2.5 },
                py: { xs: 1.5, sm: 1.75 },
                borderRadius: 3,
                backgroundColor: '#fff',
                border: `1px solid ${alpha('text', 0.10)}`,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 1.5 },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                    <LocalOffer sx={{ color: PALETTE.primary }} />
                    <Typography sx={{ fontWeight: 600, color: PALETTE.text }}>
                        Have a promo code?
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexGrow: 1 }}>
                    <TextField
                        size="small"
                        placeholder="e.g. EMMA"
                        value={inputValue}
                        onChange={(e) => {
                            setInputValue(e.target.value);
                            if (lastSubmitFailed) setLastSubmitFailed(false);
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleApply(); }}
                        sx={{ flexGrow: 1, minWidth: 120 }}
                        inputProps={{
                            style: { textTransform: 'uppercase', letterSpacing: '0.05em' },
                            'aria-label': 'Promo code',
                        }}
                    />
                    <Button
                        onClick={handleApply}
                        variant="contained"
                        disabled={!inputValue.trim()}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                        Apply
                    </Button>
                </Box>
            </Box>
            <Collapse in={lastSubmitFailed}>
                <Typography
                    variant="caption"
                    sx={{ color: PALETTE.primary, display: 'block', mt: 1 }}
                >
                    Sorry — that code isn't active right now.
                </Typography>
            </Collapse>
            <Typography
                variant="caption"
                sx={{ color: PALETTE.textMuted, display: 'block', mt: 1 }}
            >
                Promo codes unlock a discounted version of every product. The lower price shows up
                on the Zazzle product page — there's no coupon field at checkout.
            </Typography>
        </Box>
    );
};

export default PromoCodeBar;
